// Everflow click-tracking integration.
//
// initEverflow() is called once at app boot. It loads main.js from the
// configured Everflow base URL, configures organic click attribution and
// fires EF.click with renamed URL params (cmpid → offer_id, pubid →
// affiliate_id, ef_transaction_id → transaction_id). The transaction id
// returned by EF.click is persisted to the `transaction_id` cookie for
// 30 days so signup/login can read it later.
//
// getEfTransactionId() returns the EF transaction id that should be sent
// to the backend on signup/login: URL `?ef_transaction_id=...` first, then
// the `transaction_id` cookie. If the visitor came in with `?clickid=...`
// (a non-EF tracker), any stale EF cookies are dropped and an empty string
// is returned so a previous EF transaction never leaks into the payload.

// Public tracking config — safe to hardcode as a fallback. `.env*` is gitignored,
// so without these defaults EF would silently never load in production.
const BASE_URL = process.env.REACT_APP_EVERFLOW_BASE_URL || 'https://trk.swipey.me'
const ORGANIC_OFFER_ID = process.env.REACT_APP_EVERFLOW_ORGANIC_OFFER_ID || '2'
const ORGANIC_PARTNER_ID = process.env.REACT_APP_EVERFLOW_ORGANIC_PARTNER_ID || '3'

const clearEfCookies = () => {
  document.cookie = 'transaction_id=; path=/; max-age=0; SameSite=Lax'
  document.cookie = 'ef_transaction_id=; path=/; max-age=0; SameSite=Lax'
}

const writeTransactionCookie = (transactionId) => {
  document.cookie = `transaction_id=${transactionId}; path=/; max-age=2592000; SameSite=Lax`
  document.cookie = `ef_transaction_id=${transactionId}; path=/; max-age=2592000; SameSite=Lax`
}

export const initEverflow = () => {
  if (typeof window === 'undefined' || !BASE_URL) return

  try {
    const params = new URLSearchParams(window.location.search)
    if (params.get('clickid')) clearEfCookies()
  } catch (e) {}

  const s = document.createElement('script')
  s.src = `${BASE_URL}/scripts/main.js`
  s.onload = () => {
    if (typeof window.EF === 'undefined') return

    if (ORGANIC_OFFER_ID && ORGANIC_PARTNER_ID) {
      window.EF.configure({
        organic: {
          offer_id: Number(ORGANIC_OFFER_ID),
          affiliate_id: Number(ORGANIC_PARTNER_ID),
        },
      })
    }

    const result = window.EF.click({
      offer_id: window.EF.urlParameter('cmpid'),
      affiliate_id: window.EF.urlParameter('pubid'),
      source_id: window.EF.urlParameter('source_id'),
      sub1: window.EF.urlParameter('sub1'),
      sub2: window.EF.urlParameter('sub2'),
      sub3: window.EF.urlParameter('sub3'),
      sub4: window.EF.urlParameter('sub4'),
      sub5: window.EF.urlParameter('sub5'),
      transaction_id: window.EF.urlParameter('ef_transaction_id'),
    })

    if (result && typeof result.then === 'function') {
      result
        .then((transactionId) => {
          if (transactionId) writeTransactionCookie(transactionId)
        })
        .catch((err) => {
          console.warn('[everflow] EF.click rejected:', err)
        })
    }
  }
  document.head.appendChild(s)
}

const readCookie = (name) => {
  if (typeof document === 'undefined') return null
  const re = new RegExp(`(?:^|;\\s*)${name}=([^;]+)`)
  const match = document.cookie.match(re)
  return match ? decodeURIComponent(match[1]) : null
}

export const getEfTransactionId = () => {
  let urlParams
  try {
    urlParams = new URLSearchParams(window.location.search)
  } catch (e) {
    urlParams = null
  }

  if (urlParams && urlParams.get('clickid') && typeof document !== 'undefined') {
    clearEfCookies()
    return ''
  }

  // Empty URL value falls through to cookie lookup (truthy check).
  const fromUrl = urlParams ? urlParams.get('ef_transaction_id') : null
  if (fromUrl) return fromUrl

  // Try both names: `transaction_id` (set by EF.click .then callback) and
  // `ef_transaction_id` (set by backend via Set-Cookie).
  return readCookie('ef_transaction_id') || readCookie('transaction_id') || ''
}
